import React, { useEffect, useReducer, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { initialState, reducer } from '../../reducers/containerReducer';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import { PARTNER_TYPES_DICTIONARY_QUERY } from 'features/partnerTypes/components/tabs/queries';
import { INCOME_SOURCE_QUERY, PERIOD_TYPES_QUERY, DATA_TYPES_QUERY, INCOME_SOURCE_AREAS_QUERY } from './queries';
import { ADD_UPDATE_INCOME_SOURCE, ADD_UPDATE_INCOME_SOURCE_DETAIL, DELETE_INCOME_SOURCE_DETAIL } from './mutations';
import { INCOME_SOURCE_UPDATED, INCOME_SOURCE_DETAIL_UPDATED, INCOME_SOURCE_DETAIL_DELETED } from './subscriptions';

import IncomeSourceGeneralDataCard from './IncomeSourceGeneralDataCard';
import IncomeSourceDetailListCard from './IncomeSourceDetailListCard';
import { useValidation } from '@totalsoft/pure-validations-react';
import { incomeSourceValidator, incomeSourceDetailValidator } from 'features/incomeSources/validators';
import { useToast } from 'hooks/toasts/useToast';

const IncomeSourceContainer = ({ id, onChangeIncomeItemTitle, onRedirect }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [incomeSourceValidation, validateIncomeSource] = useValidation(incomeSourceValidator);
  const [incomeSourceDetailsValidation, validateIncomeSourceDetails] = useValidation(incomeSourceDetailValidator);

  const { data, loading } = useQuery(INCOME_SOURCE_QUERY, {
    variables: { id },
    skip: id === undefined
  });

  const { data: periodTypesData } = useQuery(PERIOD_TYPES_QUERY);

  const { data: dataTypesData } = useQuery(DATA_TYPES_QUERY);

  const { data: incomeSourceAreasData } = useQuery(INCOME_SOURCE_AREAS_QUERY);

  const { data: partnerTypesData } = useQuery(PARTNER_TYPES_DICTIONARY_QUERY);

  const [addOrUpdateIncomeSource] = useMutation(ADD_UPDATE_INCOME_SOURCE);

  const [addOrUpdateIncomeSourceDetail] = useMutation(ADD_UPDATE_INCOME_SOURCE_DETAIL);

  const [deleteIncomeSourceDetail] = useMutation(DELETE_INCOME_SOURCE_DETAIL);

  useSubscription(INCOME_SOURCE_UPDATED, {
    onData: data => {
      addToast(t('IncomeSources.IncomeSourceUpdated'), 'success');
      if (id === undefined) onRedirect(data.data.data.incomeSourceUpdated.incomeSource.id);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedIncomeSources' });
      client.cache.gc();
    }
  });

  useSubscription(INCOME_SOURCE_DETAIL_UPDATED, {
    onData: data => {
      addToast(t('IncomeSources.IncomeSourceDetailUpdated'), 'success');
      client.cache.evict({
        id: `IncomeSourceItem:${data.data.data.incomeSourceDetailUpdated.incomeSourceDetail.incomeSourceId}`
      });
      client.cache.gc();
    }
  });

  useSubscription(INCOME_SOURCE_DETAIL_DELETED, {
    onData: data => {
      addToast(t('IncomeSources.IncomeSourceDetailDeleted'), 'success');
      client.cache.evict({ id: `IncomeSourceDetailItem:${data.data.data.incomeSourceDetailDeleted.incomeSourceDetailId}` });
      client.cache.gc();
    }
  });

  useEffect(() => {
    if (!id) dispatch({ type: 'OnIncomeSourceAdded' });
    else if (data && data.incomeSource) dispatch({ type: 'OnInitState', income: data.incomeSource });
  }, [data, id]);

  useEffect(() => {
    if (localState.header.name) {
      onChangeIncomeItemTitle(localState.header.name);
    }
  }, [localState.header.name, onChangeIncomeItemTitle]);

  const onSave = useCallback(
    (type, data, index) => {
      switch (type) {
        case 'HeaderAddOrUpdate': {
          // eslint-disable-next-line no-unused-vars
          const { changed, ...incomeSource } = localState.header;
          if (!validateIncomeSource(localState.header)) return;
          dispatch({ type: 'OnHeaderSaved' });
          return addOrUpdateIncomeSource({
            variables: { input: incomeSource }
          });
        }
        case 'DetailAddOrUpdate': {
          // eslint-disable-next-line no-unused-vars
          const { changed, isExpanded, ...incomeSourceDetail } = data;
          dispatch({ type: 'OnDetailSaved', index });
          return addOrUpdateIncomeSourceDetail({
            variables: { input: incomeSourceDetail }
          });
        }
        case 'DetailDelete':
          dispatch({ type: 'OnDetailDeleted', index });
          return deleteIncomeSourceDetail({
            variables: { input: { id: data.id } }
          });
        default:
          return;
      }
    },
    [addOrUpdateIncomeSource, addOrUpdateIncomeSourceDetail, deleteIncomeSourceDetail, localState.header, validateIncomeSource]
  );

  useEffect(() => {
    validateIncomeSource(localState.header);
  }, [localState.header, validateIncomeSource]);

  useEffect(() => {
    validateIncomeSourceDetails(localState.details);
  }, [localState.details, validateIncomeSourceDetails]);

  return loading || localState === initialState ? (
    <Card>
      <FakeText lines={10} />
    </Card>
  ) : (
    <>
      <IncomeSourceGeneralDataCard
        incomeItem={localState.header}
        dispatch={dispatch}
        onSave={onSave}
        incomeSourceAreas={incomeSourceAreasData ? incomeSourceAreasData.incomeSourceAreas : []}
        partnerTypes={partnerTypesData ? partnerTypesData.partnerTypesDictionary : []}
        validation={incomeSourceValidation}
      />
      {id !== null && id !== undefined && (
        <IncomeSourceDetailListCard
          incomeItemDetails={localState.details}
          dispatch={dispatch}
          periodTypes={periodTypesData ? periodTypesData.periodTypes : []}
          dataTypes={dataTypesData ? dataTypesData.dataTypes : []}
          onSave={onSave}
          validation={incomeSourceDetailsValidation}
        />
      )}
    </>
  );
};

IncomeSourceContainer.propTypes = {
  id: PropTypes.number,
  onChangeIncomeItemTitle: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired
};

export default IncomeSourceContainer;
